import React, { useEffect, useRef, useState } from 'react';
import { handleImageError, PLACEHOLDER_IMAGES } from '../utils/imageUtils';

/**
 * Gambar dengan lazy loading + placeholder shimmer.
 *
 * Kenapa perlu:
 *  - Foto produk disajikan langsung dari backend dalam ukuran asli, sehingga
 *    halaman katalog yang berisi puluhan kartu produk terasa berat kalau
 *    semua gambar diunduh sekaligus saat halaman dibuka.
 *  - `loading="lazy"` membuat browser menunda unduhan sampai gambar mendekati
 *    viewport, dan `decoding="async"` mencegah proses decode memblokir render.
 *  - Selama gambar belum selesai dimuat, elemen menampilkan animasi shimmer
 *    supaya area gambar tidak terlihat kosong/putih.
 *
 * Pemakaian sama seperti <img> biasa:
 *   <LazyImage src={url} alt="..." className="w-full h-full object-cover" />
 *
 * Props tambahan:
 *   eager  - true untuk gambar di atas layar (hero / gambar utama produk),
 *            supaya TIDAK di-lazy load dan justru diprioritaskan browser.
 *   type   - jenis gambar untuk placeholder cadangan bila gagal dimuat
 *            ('product' | 'category' | 'banner' | 'user').
 */
const LazyImage = ({
  src,
  alt = '',
  type = 'product',
  className = '',
  eager = false,
  onLoad,
  onError,
  ...props
}) => {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // Gambar yang sudah ada di cache browser bisa selesai dimuat sebelum React
  // sempat memasang handler onLoad. Tanpa pengecekan ini, shimmer-nya nyangkut.
  useEffect(() => {
    setLoaded(false);
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const handleLoad = (event) => {
    setLoaded(true);
    if (onLoad) onLoad(event);
  };

  const handleError = (event) => {
    // Tetap tandai selesai supaya shimmer berhenti walaupun gambar gagal
    setLoaded(true);
    handleImageError(event, type);
    if (onError) onError(event);
  };

  return (
    <img
      ref={imgRef}
      src={src || PLACEHOLDER_IMAGES[type] || PLACEHOLDER_IMAGES.noImage}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      {...(eager ? { fetchpriority: 'high' } : {})}
      onLoad={handleLoad}
      onError={handleError}
      className={`lazy-img${loaded ? ' is-loaded' : ''}${className ? ` ${className}` : ''}`}
      {...props}
    />
  );
};

export default LazyImage;
